import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Table, Card, Typography, Tag, Space, Select, Drawer, Descriptions,
} from 'antd';
import dayjs from 'dayjs';
import apiClient from '@/services/api';

const { Title } = Typography;

interface CertRow {
  idx: string; serial: string; commonName: string; issuedAt: string; expiresAt: string;
  revokedAt: string | null; revokedReason: string | null; revokedBy: string | null;
  userIdx: string; userName: string; userRole: string | null; userOrgType: string | null;
  status: 'active' | 'expired' | 'revoked';
}

interface TokenRow {
  token: string; issuedAt: string; expiresAt: string; usedAt: string | null;
  usedFromIp: string | null; purpose: string; verificationMethod: string | null;
  issuedBy: string; status: 'used' | 'expired' | 'pending';
}

export default function AdminP12CertsPage() {
  const { t } = useTranslation();
  const [revoked, setRevoked] = useState<string | undefined>(undefined);
  const [detail, setDetail] = useState<CertRow | null>(null);

  const certs = useQuery({
    queryKey: ['admin', 'p12-certs', revoked],
    queryFn: async () => {
      const params: any = { limit: 200 };
      if (revoked) params.revoked = revoked;
      return (await apiClient.get<any>('/admin/p12-certs', params)) as any;
    },
  });

  const tokens = useQuery({
    queryKey: ['admin', 'p12-certs', detail?.idx, 'tokens'],
    enabled: !!detail,
    queryFn: async () => (await apiClient.get<any>(`/admin/p12-certs/${detail!.idx}/download-tokens`)) as any,
  });

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{t('admin.certsTitle')}</Title>
        <Select placeholder="All" allowClear style={{ width: 140 }} value={revoked} onChange={setRevoked}
          options={[{ label: 'Active', value: 'false' }, { label: 'Revoked', value: 'true' }]} />
      </Space>

      <Card>
        <Table<CertRow> size="small" rowKey="idx" loading={certs.isLoading}
          dataSource={certs.data?.certs ?? []} pagination={{ pageSize: 50 }}
          onRow={(r) => ({ onClick: () => setDetail(r), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'User', dataIndex: 'userName', key: 'u', width: 180 },
            { title: 'CN', dataIndex: 'commonName', key: 'cn', width: 120 },
            { title: 'Org', dataIndex: 'userOrgType', key: 'ot', width: 110, render: (v: string) => <Tag>{v ?? '—'}</Tag> },
            { title: 'Role', dataIndex: 'userRole', key: 'r', width: 170, render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
            { title: 'Issued', dataIndex: 'issuedAt', key: 'ia', width: 110, render: (v: string) => dayjs(v).format('YYYY-MM-DD') },
            { title: 'Expires', dataIndex: 'expiresAt', key: 'ea', width: 110, render: (v: string) => dayjs(v).format('YYYY-MM-DD') },
            { title: 'Status', dataIndex: 'status', key: 's', width: 100, render: (s: string) =>
              s === 'active' ? <Tag color="green">Active</Tag> : s === 'revoked' ? <Tag color="red">Revoked</Tag> : <Tag>Expired</Tag>
            },
            { title: 'Reason', dataIndex: 'revokedReason', key: 'rr', width: 130, render: (v: string | null) => v ?? '—' },
          ]}
        />
      </Card>

      <Drawer width={640} open={!!detail} onClose={() => setDetail(null)} title={t('admin.certDetail')}>
        {detail && (
          <>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Serial"><code>{detail.serial}</code></Descriptions.Item>
              <Descriptions.Item label="CN">{detail.commonName}</Descriptions.Item>
              <Descriptions.Item label="User">{detail.userName} ({detail.userRole})</Descriptions.Item>
              <Descriptions.Item label="Issued">{dayjs(detail.issuedAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Expires">{dayjs(detail.expiresAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {detail.status === 'active' ? <Tag color="green">Active</Tag> : detail.status === 'revoked' ? <Tag color="red">Revoked</Tag> : <Tag>Expired</Tag>}
              </Descriptions.Item>
              {detail.revokedAt && <Descriptions.Item label="Revoked at">{dayjs(detail.revokedAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>}
              {detail.revokedReason && <Descriptions.Item label="Reason">{detail.revokedReason}</Descriptions.Item>}
            </Descriptions>

            <Title level={5}>{t('admin.downloadTokens')}</Title>
            <Table<TokenRow> size="small" rowKey="token" loading={tokens.isLoading}
              dataSource={tokens.data?.tokens ?? []} pagination={false}
              columns={[
                { title: 'Token', dataIndex: 'token', key: 't', render: (v: string) => <code>{v}</code> },
                { title: 'Purpose', dataIndex: 'purpose', key: 'p' },
                { title: 'Issued', dataIndex: 'issuedAt', key: 'ia', width: 140, render: (v: string) => dayjs(v).format('MM-DD HH:mm') },
                { title: 'Status', dataIndex: 'status', key: 's', width: 90, render: (s: string) =>
                  s === 'used' ? <Tag color="green">Used</Tag> : s === 'expired' ? <Tag>Expired</Tag> : <Tag color="blue">Pending</Tag>
                },
                { title: 'Used from IP', dataIndex: 'usedFromIp', key: 'ip', width: 130 },
              ]}
            />
          </>
        )}
      </Drawer>
    </>
  );
}
